import {
  StateRegistrationName,
  StateRegistrationNameType,
} from "store/apis/StateRegistrations";
import { colors } from "constants/colors";
import {
  RegistrationStatus,
  RegistrationStatusName,
} from "store/apis/registrations";

export const COMPLIANCE_SCORE_REG_TYPES = [
  "SECRETARY_OF_STATE",
  "REGISTERED_AGENT",
  "PAY_ROLL",
] as const;

export const COMPLIANCE_SCORE_DONE_STATUSES = [
  "REGISTERED",
  "INCORPORATED",
  "FOREIGN_QUALIFIED",
  "NOT_APPLICABLE",
] as const;

export const COMPLIANCE_SCORE_COLORS = {
  registrations: colors.purple[70],
  filings: colors.dark_green.DEFAULT,
  trackBackground: colors.neutral[10],
  strokeComplete: colors.dark_green.DEFAULT,
  strokeInProgress: colors.purple[70],
  strokeWarning: colors.orange[70],
} as const;

export const REGISTRATION_TYPES: StateRegistrationNameType[] = [
  StateRegistrationName.SECRETARY_OF_STATE,
  StateRegistrationName.REGISTERED_AGENT,
  StateRegistrationName.PAY_ROLL,
];

export type RegistrationIntent = "new" | "existing";

export const INTENT_OPTIONS: {
  value: RegistrationIntent;
  title: string;
  description: string;
}[] = [
  {
    value: "new",
    title: "Not yet — help me register",
    description:
      "Inkle will handle the full registration — SOS, registered agent, and more.",
  },
  {
    value: "existing",
    title: "Already registered — just track it",
    description:
      "I've done the registration myself. Add it to my dashboard to track deadlines.",
  },
];

export const SERVICE_OPTIONS: {
  value: StateRegistrationNameType;
  label: string;
  alreadyRegisteredDescription: string;
}[] = [
  {
    value: StateRegistrationName.SECRETARY_OF_STATE,
    label: "Secretary of state",
    alreadyRegisteredDescription:
      "If you've already filed for foreign qualification or incorporation in this state, add your SOS details here so we can track your compliance deadlines.",
  },
  {
    value: StateRegistrationName.REGISTERED_AGENT,
    label: "Registered agent",
    alreadyRegisteredDescription:
      "If you have a registered agent set up in this state, add their details so we can alert you before your service expires.",
  },
  {
    value: StateRegistrationName.PAY_ROLL,
    label: "Payroll",
    alreadyRegisteredDescription:
      "If you're already registered for state payroll taxes, add your details here so we can keep track of your filing obligations.",
  },
];

export const ADD_STATE_STEP_LABELS: Record<1 | 2 | 3, string> = {
  1: "Select state",
  2: "Registration status",
  3: "Services",
};

export const ADD_STATE_NEW_FLOW_STEP_LABELS: Record<1 | 2, string> = {
  1: "Select state",
  2: "Select services",
};

export const ADD_STATE_EXISTING_FLOW_STEP_LABELS: Record<1 | 2, string> = {
  1: "Select state",
  2: "Services",
};

export const REGISTRATION_LABELS: Record<StateRegistrationNameType, string> = {
  SECRETARY_OF_STATE: "Secretary of state",
  REGISTERED_AGENT: "Registered agent",
  PAY_ROLL: "Payroll",
};

export const REGISTRATION_CARD_LABELS: Record<
  StateRegistrationNameType,
  string
> = {
  SECRETARY_OF_STATE: "Secretary of State Registration",
  REGISTERED_AGENT: "Registered Agent Service",
  PAY_ROLL: "Payroll Registration",
};

export const REGISTRATION_DESCRIPTIONS: Record<
  StateRegistrationNameType,
  (stateName: string) => string
> = {
  SECRETARY_OF_STATE: (stateName) =>
    `A legal requirement for any business operating in ${stateName}. If you're expanding here or setting up a new entity, this is your starting point.`,
  REGISTERED_AGENT: () =>
    "Designate a reliable point of contact for legal and tax documents, required by state law.",
  PAY_ROLL: (stateName) =>
    `A state registration for managing employment taxes. If you have or plan to hire employees in ${stateName}, you'll need this before your first payroll run.`,
};

export const SHORT_REGISTRATION_LABELS: Record<
  StateRegistrationNameType,
  string
> = {
  SECRETARY_OF_STATE: "SOS",
  REGISTERED_AGENT: "Reg Agent",
  PAY_ROLL: "Payroll",
};

export const ATTENTION_LABELS: Record<
  StateRegistrationNameType,
  Partial<Record<RegistrationStatus, string>> & { default: string }
> = {
  SECRETARY_OF_STATE: {
    FOREIGN_QUALIFICATION_REQUIRED: "Foreign Q.",
    NOT_REGISTERED: "Foreign Q.",
    REQUIRED: "SOS",
    default: "SOS",
  },
  REGISTERED_AGENT: {
    default: "Reg Agent",
  },
  PAY_ROLL: {
    default: "Payroll",
  },
};

export const DONE_STATUSES: RegistrationStatus[] = [
  RegistrationStatusName.REGISTERED,
  RegistrationStatusName.FOREIGN_QUALIFIED,
  RegistrationStatusName.INCORPORATED,
];

export const STATUS_TAG_TYPE: Partial<
  Record<RegistrationStatus, "green" | "yellow" | "red" | "gray">
> = {
  REGISTERED: "green",
  INCORPORATED: "green",
  FOREIGN_QUALIFIED: "green",
  NOT_REGISTERED: "red",
  REQUIRED: "red",
  FOREIGN_QUALIFICATION_REQUIRED: "red",
  PROCESSING: "yellow",
  NOT_APPLICABLE: "gray",
};

export const INKLE_HANDLES: Record<StateRegistrationNameType, string[]> = {
  SECRETARY_OF_STATE: [
    "Prepare and file your foreign qualification application with the state",
    "Liaise with state authorities on your behalf throughout the review process",
    "Monitor processing timelines and follow up on approvals",
    "Notify you when your application is approved",
    "Store your Certificate of Authority securely in your Inkle account",
  ],
  REGISTERED_AGENT: [
    "Act as your registered agent and maintain a registered office address in this state",
    "Receive and process all official legal, tax, and government correspondence",
    "Forward documents to your team promptly, with priority handling for time-sensitive mail",
    "Monitor your registered agent registration and manage annual renewals on your behalf",
  ],
  PAY_ROLL: [
    "Register with the state payroll authority",
    "Set up employee withholding accounts",
    "Coordinate filing with your payroll provider",
    "Handle all payroll registration deadlines",
  ],
};

export const FQ_DATE_FIELD_KEY_PREFIX = "foreign_qualification_date";
export const FQ_DATE_FIELD_LABEL = "Foreign Qualification Date";

export const isFqDateAttr = (attr: { field_key: string; label: string }) =>
  attr.field_key.startsWith(FQ_DATE_FIELD_KEY_PREFIX) ||
  attr.label === FQ_DATE_FIELD_LABEL;

export const SOS_NUMBER_FIELD_KEY_PREFIX = "sos_number";
export const SOS_NUMBER_FIELD_LABEL = "SOS Number";

export const isSosNumberAttr = (attr: { field_key: string; label: string }) =>
  attr.field_key.startsWith(SOS_NUMBER_FIELD_KEY_PREFIX) ||
  attr.label === SOS_NUMBER_FIELD_LABEL;

export const MISSING_FQ_DATE_LABEL = "Foreign qualification date";
export const MISSING_SOS_NUMBER_LABEL = "SOS Number";

export const OVERDUE_FILING_MAX_WINDOW_DAYS = 180;
export const UPCOMING_FILING_WINDOW_DAYS = 60;

export const ROW_WHAT_INKLE_DOES: Record<
  StateRegistrationNameType,
  { eyebrow: string; title: string; subtitle: string }
> = {
  SECRETARY_OF_STATE: {
    eyebrow: "Foreign qualification",
    title:
      "Inkle manages your Secretary of State registration from start to finish.",
    subtitle:
      "Inkle handles your Secretary of State registration end to end — we file everything, work with the state directly, and keep you posted at each stage.",
  },
  REGISTERED_AGENT: {
    eyebrow: "Registered agent service",
    title: "Appoint Inkle as your registered agent in this state.",
    subtitle:
      "Inkle will receive all official state and legal correspondence on your behalf, forward time-sensitive documents promptly, and manage annual renewals so your registration stays current.",
  },
  PAY_ROLL: {
    eyebrow: "Payroll registration",
    title: "Inkle registers your entity for state payroll taxes.",
    subtitle:
      "Inkle handles the state authority paperwork so you can run payroll legally without the admin overhead.",
  },
};
